import React, { useState, useEffect } from "react";
import { fetchSettings, updateSettings } from "../../services/api";
import { useSettings } from "../../context/SettingsContext";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { useToast } from "../../hooks/use-toast";
import { Loader2, Plus, X, Save } from "lucide-react";

const AdminSettings = () => {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();
  const { refresh } = useSettings();

  useEffect(() => { fetchSettings().then(setForm).catch(() => {}); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setOffice = (i, k, v) => set("offices", form.offices.map((o, idx) => (idx === i ? { ...o, [k]: v } : o)));
  const addOffice = () => set("offices", [...(form.offices || []), { city: "", role: "" }]);
  const removeOffice = (i) => set("offices", form.offices.filter((_, idx) => idx !== i));

  const save = async () => {
    setSaving(true);
    try {
      const { id, _id, ...payload } = form;
      await updateSettings(payload);
      await refresh();
      toast({ title: "Contact info saved" });
    } catch (e) { toast({ title: "Save failed", variant: "destructive" }); }
    finally { setSaving(false); }
  };

  if (!form) return <div className="py-20 flex justify-center"><Loader2 className="w-8 h-8 text-[#C9A227] animate-spin" /></div>;

  return (
    <div className="max-w-2xl">
      <h2 className="font-display font-bold text-lg text-[#14171c] mb-5">Contact Information</h2>
      <div className="bg-white rounded-xl border border-neutral-200 p-6 space-y-4">
        <div className="grid md:grid-cols-2 gap-4">
          <div><Label>Email</Label><Input className="mt-1.5" value={form.email || ""} onChange={(e) => set("email", e.target.value)} /></div>
          <div><Label>Phone</Label><Input className="mt-1.5" value={form.phone || ""} onChange={(e) => set("phone", e.target.value)} /></div>
          <div><Label>WhatsApp</Label><Input className="mt-1.5" value={form.whatsapp || ""} onChange={(e) => set("whatsapp", e.target.value)} /></div>
        </div>
        <div><Label>Factory Address</Label><Input className="mt-1.5" value={form.addressCN || ""} onChange={(e) => set("addressCN", e.target.value)} /></div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <Label>Offices</Label>
            <button type="button" onClick={addOffice} className="inline-flex items-center gap-1 text-sm font-semibold text-[#C9A227]"><Plus className="w-4 h-4" /> Add office</button>
          </div>
          <div className="space-y-2">
            {(form.offices || []).map((o, i) => (
              <div key={i} className="flex gap-2">
                <Input value={o.city} onChange={(e) => setOffice(i, "city", e.target.value)} placeholder="City" />
                <Input value={o.role} onChange={(e) => setOffice(i, "role", e.target.value)} placeholder="Role" />
                <button type="button" onClick={() => removeOffice(i)} className="text-red-400 hover:text-red-600 px-1"><X className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        </div>

        <Button onClick={save} disabled={saving} className="bg-[#C9A227] hover:bg-[#A6841B] text-white">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />} Save Changes
        </Button>
      </div>
    </div>
  );
};

export default AdminSettings;
